import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { DEFAULT_QUERY_OPTIONS } from "#src/components/hooks/adapters/adapterUtils";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import { ExceptionUtils } from "#src/utils/exception";
import { linkToAssetDetailPage } from "#src/utils/links";
import { useQuery } from "@tanstack/react-query";
import { KeyValueList, KeyValuePairList } from "@validereinc/common-components";
import {
  AssetGroupAdapter,
  AssetType,
  AssetTypeType,
  DeviceAdapter,
  EquipmentAdapter,
  FacilityAdapter,
  FlowAdapter,
} from "@validereinc/domain";
import { BaseError } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useMemo, type ReactNode } from "react";
import styles from "./CustomAttributeKeyValueList.module.scss";

const cx = classNames.bind(styles);

const getAsset = async (assetType: AssetTypeType, assetId: string) => {
  switch (assetType) {
    case AssetType.FACILITY:
      return FacilityAdapter.getOne({ id: assetId });
    case AssetType.EQUIPMENT:
      return EquipmentAdapter.getOne({ id: assetId });
    case AssetType.DEVICE:
      return DeviceAdapter.getOne({ id: assetId });
    case AssetType.FLOW:
      return FlowAdapter.getOne({ id: assetId });
    case AssetType.ASSET_GROUP:
      return AssetGroupAdapter.getOne({ id: assetId });
  }
};

export const CustomAttributeKeyValueList = ({
  assetType,
  assetId,
  displayAttributes,
}: {
  assetType: AssetTypeType;
  assetId: string;
  displayAttributes: string[];
}) => {
  const { data: assetData, isLoading: assetLoading } = useQuery(
    [assetType, "getOne", { id: assetId }],
    () => {
      return getAsset(assetType, assetId);
    },
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: Boolean(assetId),
    }
  );
  const assetAttributes = assetData?.data.custom_attributes;
  const { data: definitionData, isLoading: definitionLoading } =
    useListCustomAttributes({ filters: { entity_type: assetType } });

  const data = useMemo(() => {
    if (!definitionData?.data) {
      return [];
    }

    return displayAttributes.reduce<KeyValuePairList>((acc, attribute) => {
      const definition = definitionData.data.find(
        (def) => def.field_name === attribute
      );

      if (definition) {
        const unprocessedValue = assetAttributes?.[attribute] ?? "-";
        let value: ReactNode = "-";

        if (
          typeof unprocessedValue === "object" &&
          "entity_type" in unprocessedValue
        ) {
          const lookupValue = unprocessedValue;

          const link = linkToAssetDetailPage(
            lookupValue.entity_type,
            lookupValue.id
          );

          value = <RoutingLink to={link}>{lookupValue.name}</RoutingLink>;
        }

        if (Array.isArray(unprocessedValue)) {
          value = unprocessedValue.join(", ");
        }

        if (
          typeof unprocessedValue === "number" ||
          typeof unprocessedValue === "string"
        ) {
          value = unprocessedValue;
        }

        acc.push({
          title: definition.display_name,
          value,
        });
      } else {
        ExceptionUtils.reportException(
          new BaseError(
            `Could not find attribute ${attribute} for ${assetType}`
          ),
          "error"
        );
      }

      return acc;
    }, []);
  }, [definitionData?.data, assetAttributes]);

  return (
    <KeyValueList
      data={data}
      className={cx("keyValueListContainer")}
      isLoading={(assetId && assetLoading) || definitionLoading}
    />
  );
};
